import React from 'react';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import {
  Banknote,
  CircleCheckBig,
  CreditCard,
  Gift,
  Link,
  LucideIcon,
} from 'lucide-react';
import CheckoutElementsForm from '../payments/CheckoutElementsForm';
import Badge from '../global/Badge';
import { CheckoutPaymentMethod } from '../../types/appointments';
import useTerminalReader from '../../hooks/businesses/useTerminalReader';
import { LoadedUser } from '../../interfaces';
import useUser from '../../hooks/global/useUser';
import { cx } from '../../helpers/global';
import { getPaymentMethodFee } from '../../helpers/payments';
import GiftCardPaymentMethod from './GiftCardPaymentMethod';
import { CheckoutCustomer, GiftCardTypeParam } from '../../types/payments';
import { formatDollarAmount, isNil } from '../../utils/global/helper';
import { SplitPaymentMethod } from '../../context/SplitPaymentsContext/context';
import useGetCustomerGiftCardBalance from '../../hooks/giftCards/useGetCustomerGiftCardBalance';

function labelMaker({
  method,
  isSplitPayment = false,
  splitPaymentPaymentMethods,
  selectedPaymentMethod,
  business,
  totalPriceCents,
  showFees,
  cardElementDisabled,
  giftCardAmountCents,
  giftCardType,
  customer,
  giftCardBalanceCents = 0,
  isFetchingGiftCardBalance = false,
  hasAppointment = false,
}: {
  method: CheckoutPaymentMethod;
  isSplitPayment?: boolean;
  splitPaymentPaymentMethods?: SplitPaymentMethod[];
  selectedPaymentMethod: CheckoutPaymentMethod;
  business: LoadedUser['business'];
  totalPriceCents: number;
  showFees?: boolean;
  cardElementDisabled?: boolean;
  giftCardAmountCents?: number;
  giftCardType?: GiftCardTypeParam;
  customer?: CheckoutCustomer;
  giftCardBalanceCents?: number;
  isFetchingGiftCardBalance?: boolean;
  hasAppointment?: boolean;
}) {
  const isChecked = selectedPaymentMethod === method;

  let icon: LucideIcon | string = CircleCheckBig;
  let labelName = '';

  switch (method) {
    case 'CREDIT':
      icon = Link;
      labelName = 'Send payment link to customer';
      break;
    case 'TERMINAL':
      icon = '/pos.svg';
      labelName = 'Send to payment terminal';
      break;
    case 'MANUAL':
      icon = CreditCard;
      labelName = 'Manually enter card';
      break;
    case 'CASH':
      icon = Banknote;
      labelName = 'Record cash payment';
      break;
    case 'DIGITAL':
      icon = '/digital-pay.svg';
      labelName = 'Record digital payment (Zelle/Venmo, etc.)';
      break;
    case 'FREE':
      icon = CircleCheckBig;
      labelName = hasAppointment
        ? 'Checkout for $0 (mark appointment completed)'
        : 'Checkout for $0';
      break;
    case 'GIFT_CARD':
      icon = Gift;
      labelName =
        !customer?.customerId && !customer?.phoneNumber
          ? 'Record non-Porter gift card payment'
          : 'Record Porter gift card payment';
      break;
    default:
      labelName = '';
  }

  const Icon = icon as LucideIcon;
  const feeId = `payment-method-fee-${method}`;
  const methodFeeRate = getPaymentMethodFee(method, business);

  return (
    <Stack
      gap={1}
      width="100%"
      sx={{
        [`#${feeId}`]: {
          opacity: isChecked ? 1 : undefined,
        },
        [`&:hover #${feeId}`]: {
          opacity: 1,
        },
      }}>
      <Box className="size-label" width="100%">
        {typeof icon === 'string' ? (
          <img
            src={icon}
            alt=""
            style={{ width: 24, height: 24, flexShrink: 0 }}
          />
        ) : (
          <Icon
            size={22}
            strokeWidth={1.75}
            color={isChecked ? 'var(--primary)' : 'var(--darkGrey)'}
            style={{ flexShrink: 0 }}
          />
        )}
        <Stack
          mr={0}
          width="100%"
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          className="size-label-text">
          <Typography variant="h5" component="p">
            {labelName}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            {method === 'CREDIT' && business?.isBnplEnabled && (
              <>
                <img
                  src="/afterpay.svg"
                  alt="Afterpay"
                  style={{ width: '16px', height: '16px' }}
                />
                <img
                  src="/affirm.svg"
                  alt="Affirm"
                  style={{ width: '21px', height: '16px' }}
                />
                <Badge
                  text="BNPL Enabled"
                  icon={CheckCircle}
                  colorScheme="secondary"
                />
              </>
            )}
            {method === 'GIFT_CARD' &&
              !isFetchingGiftCardBalance &&
              !!(customer?.customerId || customer?.phoneNumber) && (
                <Badge
                  text={`${formatDollarAmount(
                    giftCardBalanceCents / 100,
                    true,
                  )} Available Balance`}
                  colorScheme="secondary"
                />
              )}
            {showFees && methodFeeRate > 0 && (
              <Typography
                id={feeId}
                variant="body2"
                component="p"
                sx={{
                  opacity: 0,
                  transition: 'opacity 0.25s ease-in-out',
                }}>
                {(methodFeeRate * 100).toFixed(2)}%
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>
      {method === 'MANUAL' && selectedPaymentMethod === 'MANUAL' && (
        <CheckoutElementsForm disabled={cardElementDisabled} />
      )}
      {method === 'GIFT_CARD' &&
        selectedPaymentMethod === 'GIFT_CARD' &&
        customer &&
        giftCardType &&
        !isNil(giftCardAmountCents) && (
          <GiftCardPaymentMethod
            isSplitPayment={isSplitPayment}
            totalPriceCents={totalPriceCents}
            amountCents={giftCardAmountCents ?? 0}
            giftCardType={giftCardType}
            customer={customer}
            splitPaymentPaymentMethods={splitPaymentPaymentMethods}
          />
        )}
    </Stack>
  );
}

const PAYMENT_METHODS_ORDER: CheckoutPaymentMethod[] = [
  'TERMINAL',
  'CREDIT',
  'MANUAL',
  'CASH',
  'DIGITAL',
  'GIFT_CARD',
  'FREE',
];

type Props = {
  isSplitPayment?: boolean;
  splitPaymentPaymentMethods?: SplitPaymentMethod[];
  paymentMethods: CheckoutPaymentMethod[];
  selectedPaymentMethod: CheckoutPaymentMethod;
  onChange: (value: CheckoutPaymentMethod | '') => void;
  totalPriceCents: number;
  giftCardAmountCents?: number;
  giftCardType?: GiftCardTypeParam;
  customer?: CheckoutCustomer;
  showFees?: boolean;
  disabled?: boolean;
  cardElementDisabled?: boolean;
  hasAppointment?: boolean;
};

function PaymentMethodsList({
  isSplitPayment = false,
  splitPaymentPaymentMethods,
  paymentMethods,
  selectedPaymentMethod,
  onChange,
  showFees = false,
  disabled = false,
  cardElementDisabled = false,
  totalPriceCents,
  giftCardAmountCents,
  giftCardType,
  customer,
  hasAppointment = false,
}: Props) {
  const { user } = useUser<LoadedUser>();
  const { data: terminal } = useTerminalReader();
  const {
    data: giftCardBalanceData,
    isLoading: isLoadingGiftCardBalance,
    isPlaceholderData: isPlaceholderGiftCardBalance,
  } = useGetCustomerGiftCardBalance({
    customer: {
      customerId: customer?.customerId,
      phoneNumber: customer?.phoneNumber,
    },
  });

  const giftCardBalanceCents = giftCardBalanceData?.availableBalance ?? 0;

  const terminalIsOnline = React.useMemo(
    () => terminal?.status === 'online',
    [terminal],
  );

  const sortedPaymentMethods = paymentMethods?.sort(
    (a, b) =>
      PAYMENT_METHODS_ORDER.indexOf(a) - PAYMENT_METHODS_ORDER.indexOf(b),
  );

  return (
    <RadioGroup
      aria-labelledby="radio-payment-method"
      name="paymentMethod"
      id="payment-method-radio-group"
      value={selectedPaymentMethod}
      onChange={async (_event, value) => {
        onChange(value as CheckoutPaymentMethod);
      }}>
      {sortedPaymentMethods?.map(method => {
        if (method === 'TERMINAL' && !terminalIsOnline) return null;
        const isChecked = selectedPaymentMethod === method;
        return (
          <FormControlLabel
            key={method}
            disabled={disabled}
            value={method}
            control={
              <Radio
                inputProps={{ className: 'radio-payment-method' }}
                onClick={() => {
                  if (method === selectedPaymentMethod) {
                    onChange('');
                  }
                }}
              />
            }
            label={labelMaker({
              method,
              isSplitPayment,
              selectedPaymentMethod,
              showFees,
              business: user.business,
              cardElementDisabled,
              totalPriceCents,
              giftCardAmountCents,
              giftCardType,
              customer,
              splitPaymentPaymentMethods,
              giftCardBalanceCents,
              isFetchingGiftCardBalance:
                isLoadingGiftCardBalance || isPlaceholderGiftCardBalance,
              hasAppointment,
            })}
            className={cx(
              'size-label',
              'md',
              isChecked && 'checked',
              disabled && 'disabled',
            )}
          />
        );
      })}
    </RadioGroup>
  );
}

export default PaymentMethodsList;
